import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, Calendar, ArrowRight } from 'lucide-react';
import { Article } from '../types/article';

interface ArticleCardProps {
  article: Article;
  metadata?: Article; // Optional pre-fetched metadata
}

const ArticleCard: React.FC<ArticleCardProps> = ({ article, metadata }) => {
  const { i18n, t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  // Consistently use i18n language as the source of truth
  const currentLangFromI18n = i18n.language.split('-')[0];
  
  // Always use the metadata provided by the parent component
  // This eliminates redundant fetching and ensures consistency with the selected language
  const displayMetadata = metadata || article;

  return (
    <Link
      to={`/${lang || currentLangFromI18n}/articles/${article.slug}`}
      className="bg-gray-50 rounded-card shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col h-full"
    >
      <div className="overflow-hidden">
        <img 
          src={displayMetadata.imageUrl || article.imageUrl} 
          srcSet={displayMetadata.imageUrl2x ? `${displayMetadata.imageUrl} 600w, ${displayMetadata.imageUrl2x} 1200w` : undefined}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 400px"
          alt={displayMetadata.title || article.title} 
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
        />
      </div>
      <div className="px-8 py-6 flex flex-col flex-grow">
        <div className="flex items-center mb-4">
          <span className="article-tag">
            {displayMetadata.category || article.category}
          </span>
          <span className="mx-2 text-gray-400">•</span>
          <span className="flex items-center text-gray-700 text-sm">
            <Clock size={12} className="mr-1" aria-hidden="true" />
            {displayMetadata.readTime || article.readTime}
          </span>
        </div>
        <h2 className="text-xl mb-2 line-clamp-2 mt-0.5">
          {displayMetadata.title || article.title}
        </h2>
        <p className="mb-4 line-clamp-3 flex-grow">
          {displayMetadata.description || article.description}
        </p>
        <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-300">
          <span className="text-gray-700 text-sm flex items-center">
            <Calendar size={14} className="mr-1" aria-hidden="true" />
            {displayMetadata.date || article.date}
          </span>
          <span className="text-primary font-medium hover:underline inline-flex items-center">
            {t('articles.readMore')}<span className="sr-only">: {displayMetadata.title}</span>
            <ArrowRight size={16} className="ml-1" aria-hidden="true" />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ArticleCard;